// usePushSubscription — the frontend half of web-push-notifications (design
// D7/D9/D10). It owns the browser-side Service Worker + Push subscription
// lifecycle and nothing else: register /sw.js, drive pushManager.subscribe,
// and mirror the resulting device row to the backend via the v2 apiClient.
//
// Three surfaces share it, so state lives in module-singleton refs (the same
// cross-surface pattern as useAddFundsNavigation):
//   - App.vue calls initializePostLogin() once the user is signed in. It
//     registers the SW and re-affirms an EXISTING subscription, but NEVER
//     prompts for permission.
//   - Account Settings (th-o1plk.6) calls subscribe()/unsubscribe() from an
//     explicit user click — the ONLY place a permission prompt may originate.
//   - The authTransition hook (th-f79yz.9) calls revokeCurrentDevice() on
//     sign-out / account-switch.
//
// Every browser-API touchpoint (navigator, window/PushManager, Notification)
// is injectable via `deps` so the whole flow is unit-testable under jsdom with
// mocks. The runtime kill switch (WEB_PUSH_ENABLED) and non-secret VAPID key
// arrive through the apiVersioning feature-flag seam, not import.meta.env.
//
// `deviceStatus` is the ONE derivation of this device's push status (a
// PUSH_DEVICE_STATUS value), so no surface re-derives it from the flag,
// support, permission, and subscription pieces (th-bncqr).
import { ref, computed } from 'vue'
import { useApiClient } from '@/services/apiClient'
import { useApiVersioning } from '@/stores/apiVersioning'

const SERVICE_WORKER_URL = '/sw.js'

// This device's push status, in precedence order. Each value names the one
// remedy a surface can offer: only `unsubscribed` is finishable in Account
// Settings; `blocked` is undone in the browser's site settings; `unsupported`
// and `disabled` have no remedy on this device.
export const PUSH_DEVICE_STATUS = Object.freeze({
  disabled: 'disabled', // WEB_PUSH_ENABLED kill switch off: no push UI anywhere
  unsupported: 'unsupported', // browser lacks SW / PushManager / Notification
  blocked: 'blocked', // Notification.permission === 'denied'
  subscribed: 'subscribed', // this device holds an active subscription
  unsubscribed: 'unsubscribed', // the ONLY state Account Settings can finish
})

// Module-singleton reactive state, shared across every caller of the composable.
const permission = ref('default')
const isSubscribed = ref(false)
const currentSubscriptionId = ref(null)
const registration = ref(null)

// Convert a base64url VAPID public key into the Uint8Array that
// pushManager.subscribe requires for `applicationServerKey`. Exported so it can
// be unit-tested in isolation.
export function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4)
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/')
  const rawData = atob(base64)
  const output = new Uint8Array(rawData.length)
  for (let i = 0; i < rawData.length; i += 1) {
    output[i] = rawData.charCodeAt(i)
  }
  return output
}

export function usePushSubscription(deps = {}) {
  const nav = deps.navigator || (typeof navigator !== 'undefined' ? navigator : undefined)
  const notificationApi =
    deps.Notification || (typeof Notification !== 'undefined' ? Notification : undefined)
  const win = deps.window || (typeof window !== 'undefined' ? window : undefined)
  const apiClient = deps.apiClient || useApiClient()
  const apiVersioning = deps.apiVersioning || useApiVersioning()

  // Push requires a Service Worker container, a PushManager, and the
  // Notification permission API. Any missing piece makes the whole flow a no-op.
  const isSupported = computed(() =>
    Boolean(
      nav &&
        nav.serviceWorker &&
        win &&
        typeof win.PushManager !== 'undefined' &&
        notificationApi &&
        typeof notificationApi.requestPermission === 'function',
    ),
  )

  // Precedence: disabled > unsupported > blocked > subscribed > unsubscribed.
  // The kill-switch arm is the same `webPushEnabled` gate every other push
  // surface reads. `permission` is the module-singleton ref, so a caller that
  // runs syncPermission() re-derives the status from the live browser value.
  const deviceStatus = computed(() => {
    if (!apiVersioning.webPushEnabled) return PUSH_DEVICE_STATUS.disabled
    if (!isSupported.value) return PUSH_DEVICE_STATUS.unsupported
    if (permission.value === 'denied') return PUSH_DEVICE_STATUS.blocked
    if (isSubscribed.value) return PUSH_DEVICE_STATUS.subscribed
    return PUSH_DEVICE_STATUS.unsubscribed
  })

  function syncPermission() {
    permission.value = notificationApi ? notificationApi.permission : 'default'
    return permission.value
  }

  // Idempotent: registering /sw.js more than once returns the same worker, so
  // we cache the registration for the pushManager reads that follow.
  async function registerServiceWorker() {
    if (registration.value) return registration.value
    const reg = await nav.serviceWorker.register(SERVICE_WORKER_URL)
    registration.value = reg
    return reg
  }

  async function resolveRegistration() {
    if (registration.value) return registration.value
    if (nav && nav.serviceWorker && typeof nav.serviceWorker.getRegistration === 'function') {
      const reg = await nav.serviceWorker.getRegistration()
      if (reg) registration.value = reg
      return reg || null
    }
    return null
  }

  // Shape a browser PushSubscription into the backend's create contract. The
  // controller permits exactly these snake_case keys and pins user_id to the
  // signed-in user server-side (th-f79yz.4).
  function serializeSubscription(browserSub) {
    const json =
      browserSub && typeof browserSub.toJSON === 'function' ? browserSub.toJSON() : browserSub || {}
    const keys = json.keys || {}
    return {
      endpoint: json.endpoint,
      p256dh_key: keys.p256dh,
      auth_key: keys.auth,
      user_agent: nav && nav.userAgent ? nav.userAgent : null,
    }
  }

  // POST /api/v2/push_subscriptions (upsert-by-endpoint) and hold the returned
  // row id so a later revoke can DELETE by id.
  async function persistSubscription(browserSub) {
    const row = await apiClient.post('/push_subscriptions', serializeSubscription(browserSub))
    if (row && row.id != null) currentSubscriptionId.value = row.id
    return row
  }

  // Re-affirm ownership of an already-granted subscription without prompting.
  // The POST is an upsert-by-endpoint, so this also implements the D3
  // account-switch re-key backstop and refreshes the id used by revoke.
  async function refreshExistingSubscription() {
    const reg = await resolveRegistration()
    if (!reg) return null
    const existing = await reg.pushManager.getSubscription()
    if (!existing) {
      isSubscribed.value = false
      return null
    }
    const row = await persistSubscription(existing)
    isSubscribed.value = true
    return row
  }

  // Post-login bootstrap from App.vue. Registers the SW when push is enabled
  // and supported; re-affirms an existing subscription ONLY when permission was
  // already granted. It NEVER requests permission — that is reserved for the
  // explicit opt-in in subscribe().
  async function initializePostLogin() {
    if (!apiVersioning.webPushEnabled || !isSupported.value) return false
    await registerServiceWorker()
    syncPermission()
    if (permission.value === 'granted') {
      await refreshExistingSubscription()
    }
    return true
  }

  // Explicit opt-in — the only path that may prompt for permission, and only
  // ever as the direct result of a user click (Account Settings). Returns the
  // created row, or null if unsupported / disabled / permission refused.
  async function subscribe() {
    if (!apiVersioning.webPushEnabled || !isSupported.value) return null
    const reg = await registerServiceWorker()

    if (notificationApi.permission !== 'granted') {
      const result = await notificationApi.requestPermission()
      syncPermission()
      if (result !== 'granted') return null
    } else {
      syncPermission()
    }

    const vapidKey = apiVersioning.vapidPublicKey
    if (!vapidKey) return null

    const browserSub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(vapidKey),
    })
    const row = await persistSubscription(browserSub)
    isSubscribed.value = true
    return row
  }

  // Explicit unsubscribe (Account Settings). Deletes the backend row first so a
  // successful browser unsubscribe never orphans a server row, then tears down
  // the browser subscription.
  //
  // The DELETE is `keepalive`: it is also the revocation an account switch runs
  // on a page that is about to re-boot, and a page committed to a full page
  // load sends only `keepalive` requests (th-0qphb,
  // `services/sessionToken.js#leavePage`). Without it the DELETE would never
  // settle there, and the browser unsubscribe that waits on it would never run.
  async function unsubscribe() {
    if (currentSubscriptionId.value != null) {
      try {
        await apiClient.delete(`/push_subscriptions/${currentSubscriptionId.value}`, { keepalive: true })
      } catch {
        // A missing/foreign row 404s — harmless; the browser unsubscribe below
        // is the source of truth for this device.
      }
    }
    const reg = await resolveRegistration()
    if (reg) {
      const browserSub = await reg.pushManager.getSubscription()
      if (browserSub) await browserSub.unsubscribe()
    }
    isSubscribed.value = false
    currentSubscriptionId.value = null
  }

  // Fire-and-forget teardown for sign-out / account-switch (th-f79yz.9). Runs
  // in the narrow window before a full-page reload, so it swallows errors; the
  // D3 upsert-by-endpoint re-key is the backstop for any missed revocation.
  async function revokeCurrentDevice() {
    try {
      await unsubscribe()
    } catch {
      isSubscribed.value = false
      currentSubscriptionId.value = null
    }
  }

  // Bind this device's subscription to the session lifecycle (design D9). The
  // auth store dispatches a window `authTransition` CustomEvent on sign-out and
  // account-switch; on either, revoke this device's subscription so a shared
  // browser never leaks the prior user's pushes. The revocation is fire-and-
  // forget — never awaited here — so it can never block or delay the sign-out
  // reload, and it rides the composable's own v2 apiClient (the auth store's
  // client is the v1 baseURL). `guestToUser` is intentionally ignored: a fresh
  // sign-in re-keys the existing subscription via the D3 upsert backstop rather
  // than revoking it. Returns a cleanup function that removes the listener.
  function registerAuthTransitionRevocation() {
    if (!win || typeof win.addEventListener !== 'function') return () => {}
    const handler = (event) => {
      const type = event && event.detail ? event.detail.type : undefined
      if (type === 'signOut' || type === 'userSwitch') {
        // revokeCurrentDevice already swallows its own errors; the extra catch
        // guarantees a fire-and-forget call never surfaces an unhandled
        // rejection regardless of future changes.
        revokeCurrentDevice().catch(() => {})
      }
    }
    win.addEventListener('authTransition', handler)
    return () => win.removeEventListener('authTransition', handler)
  }

  return {
    // state
    isSupported,
    permission,
    isSubscribed,
    deviceStatus,
    currentSubscriptionId,
    registration,
    // actions
    syncPermission,
    registerServiceWorker,
    initializePostLogin,
    subscribe,
    unsubscribe,
    revokeCurrentDevice,
    registerAuthTransitionRevocation,
  }
}
